// src/components/dentally/DentallyPatientSummary.jsx
//
// Read-only facts about the linked Dentally patient, shown inside the
// "DENTALLY PATIENT" card in Personal details. Ordered by what a receptionist
// needs before they pick up the phone or book:
//
//   0. Deceased — above even the medical alert. If Dentally records the patient
//      as deceased, nothing else on this card matters and phoning them is the
//      worst outcome the panel can lead to. It reads as an error-severity notice
//      whatever `active` says, because Dentally can carry the reason without the
//      archive flag.
//   1. Medical alert — the only other field here with a clinical consequence.
//   2. Open in Dentally — saves searching the provider by hand.
//   3. Site — on a multi-site account this is the difference between booking
//      into the right diary and the wrong one.
//   4. Last compared — so "we have not checked since yesterday" is legible even
//      when nothing differs.
//   5. NHS number and preferred name — ordinary rows, omitted when absent.
//
// The archived/active state is NOT rendered here: it modifies the card's
// existing connection chip (DentallyPatientLinkSection) so an archived patient
// stops looking identical to a healthy one.
//
// Nothing renders for a null summary. Every value is display-only; this card
// has never written back to Dentally.
import PropTypes from 'prop-types';
import { Alert, Box, Link, Typography } from '@mui/material';
import Iconify from '../Iconify';
import { formatCheckedAt } from '../../utils/dentallyLeadSyncPresentation';
import { deceasedPatientMessage } from '../../utils/dentallyPatientSummary';

function SummaryRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
}
SummaryRow.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

export default function DentallyPatientSummary({ summary, checkedAt }) {
  const lastChecked = formatCheckedAt(checkedAt);
  if (!summary?.hasContent && !lastChecked) return null;

  return (
    <Box data-testid="dentally-patient-summary" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
      {summary?.archivedReasonIsDeceased && (
        <Alert
          severity="error"
          icon={<Iconify icon="mdi:alert-octagon-outline" width={18} height={18} />}
          sx={{ py: 0.5, mb: 0.5 }}
          data-testid="dentally-patient-deceased"
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {deceasedPatientMessage(summary)}
          </Typography>
        </Alert>
      )}

      {summary?.medicalAlert === true && (
        <Alert
          severity="warning"
          icon={<Iconify icon="mdi:medical-bag" width={18} height={18} />}
          sx={{ py: 0.5, mb: 0.5 }}
          data-testid="dentally-patient-medical-alert"
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Medical alert
          </Typography>
          {summary.medicalAlertText && (
            <Typography variant="body2">{summary.medicalAlertText}</Typography>
          )}
        </Alert>
      )}

      {/* Tri-state: Dentally did not report the flag. Said quietly and in
          words, because silence here reads as "no alert" and it is not. */}
      {summary?.medicalAlert === null && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="dentally-patient-medical-alert-unknown"
        >
          Medical alert not reported by Dentally.
        </Typography>
      )}

      {summary?.dentallyUrl && (
        <Link
          href={summary.dentallyUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, alignSelf: 'flex-start' }}
          data-testid="dentally-patient-open-link"
        >
          Open in Dentally
          <Iconify icon="eva:external-link-fill" width={16} height={16} />
        </Link>
      )}

      {summary?.site && <SummaryRow label="Site">{summary.site.name}</SummaryRow>}

      {lastChecked && (
        <Typography variant="caption" color="text.secondary" data-testid="dentally-patient-checked-at">
          {lastChecked}.
        </Typography>
      )}

      {/* Dentally can send a reason with an active patient, so this is NOT gated
          on `active === false` — that case is the card's chip modifier and its
          own sentence. Here it is an ordinary row for the reason on its own,
          labelled: the raw value is an enum token like "no_reason_given".
          Deceased is excluded because it has the notice above. */}
      {summary?.archivedReasonLabel && summary?.active !== false && !summary?.archivedReasonIsDeceased && (
        <SummaryRow label="Archive reason">{summary.archivedReasonLabel}</SummaryRow>
      )}

      {/* Shown as the CRM shows the lead's own NHS number on this same screen —
          in full, only respaced 3-3-4. The CRM has no masking convention for
          patient identifiers. */}
      {summary?.nhsNumber && <SummaryRow label="NHS number">{summary.nhsNumber}</SummaryRow>}
      {summary?.preferredName && <SummaryRow label="Preferred name">{summary.preferredName}</SummaryRow>}
    </Box>
  );
}

DentallyPatientSummary.propTypes = {
  /** Output of normalisePatientSummary — null when unlinked or the provider read failed. */
  summary: PropTypes.shape({
    dentallyUrl: PropTypes.string,
    medicalAlert: PropTypes.bool,
    // null is a real value here — "Dentally did not report it".
    medicalAlertText: PropTypes.string,
    site: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    active: PropTypes.bool,
    archivedReason: PropTypes.string,
    archivedReasonLabel: PropTypes.string,
    archivedReasonIsDeceased: PropTypes.bool,
    nhsNumber: PropTypes.string,
    preferredName: PropTypes.string,
    hasContent: PropTypes.bool,
  }),
  /** ISO timestamp of the last CRM ↔ Dentally comparison. */
  checkedAt: PropTypes.string,
};

DentallyPatientSummary.defaultProps = {
  summary: null,
  checkedAt: null,
};
